import { useRouter } from 'next/router'

import { api, errorHandler, getUrlString, notificationHandler } from '@/lib/utils'
import { useAuth } from '@/providers/AuthProvider'
import {
  EnrollmentApplication as EnrollmentApplicationType,
  EnrollmentApplicationStage as EnrollmentApplicationStageType,
  EnrollmentCourseOffering as EnrollmentCourseOfferingType,
  Installments as InstallmentsType,
  InstitutionContractTemplate as InstitutionContractTemplateType,
  InstitutionContractTemplateEnrollmentFee as InstitutionContractTemplateEnrollmentFeeType
} from '@/types'

import useFetch from './useFetch'

export type ApplicationStepKey =
  | 'registration-data'
  | 'form'
  | 'institution-contract'
  | 'review'
  | 'success'

export interface ApplicationStep {
  key: ApplicationStepKey
  label: string
  path: string
}

const stepLabels: Record<ApplicationStepKey, string> = {
  'registration-data': 'Aluno e responsável',
  form: 'Dados complementares',
  'institution-contract': 'Condições de pagamento',
  review: 'Revisão',
  success: 'Conclusão'
}

export const getApplicationStepPath = (applicationUid: string, key: ApplicationStepKey) =>
  `/applications/${applicationUid}/${key}`

export const getApplicationStageStep = (
  stage?: EnrollmentApplicationStageType | null
): ApplicationStepKey => {
  if (stage === undefined) return 'registration-data'
  if (stage === null || stage === 'digital_signature') return 'success'
  return stage.replace(/_/g, '-') as ApplicationStepKey
}

export const getApplicationSteps = (
  applicationUid: string,
  hasNegotiationStep: boolean,
  hasFormStep = false
): ApplicationStep[] => {
  const keys: ApplicationStepKey[] = [
    'registration-data',
    ...(hasFormStep ? (['form'] as ApplicationStepKey[]) : []),
    ...(hasNegotiationStep ? (['institution-contract'] as ApplicationStepKey[]) : []),
    'review',
    'success'
  ]
  return keys.map(key => ({
    key,
    label: stepLabels[key],
    path: getApplicationStepPath(applicationUid, key)
  }))
}

export const getApplicationBillingDates = (application?: EnrollmentApplicationType | null) => {
  const { institutionContractChoices: choices, institutionContractTemplate } = application || {}
  const dueDay = choices?.dueDay || institutionContractTemplate?.dueDayOptions?.[0]?.day || null
  const dueDayOption = institutionContractTemplate?.dueDayOptions?.find(
    option => option.day === dueDay
  )
  const billingStartDate =
    dueDayOption?.firstDueDate ||
    (institutionContractTemplate?.billingStartDateType === 'fixed'
      ? institutionContractTemplate.billingStartDate
      : null) ||
    null

  return {
    dueDay,
    billingStartDate,
    billingStartDateDisplay: billingStartDate
      ? null
      : institutionContractTemplate?.billingStartDateTypeDisplay || null,
    enrollmentFeeDueDate: institutionContractTemplate?.enrollmentFee?.dueDate || null
  }
}

export const getTuitionInstallment = (
  institutionContractTemplate?: InstitutionContractTemplateType | null,
  installments?: number | null
) => {
  const {
    installmentAmount,
    negotiatedAmount,
    recurrence,
    recurrenceOptions,
    recurrenceSelectionEnabled
  } = institutionContractTemplate || {}
  const selectedInstallments = (recurrenceSelectionEnabled && installments) || recurrence || 1
  const option = recurrenceOptions?.find(item => item.installments === selectedInstallments)
  return {
    installments: selectedInstallments,
    installmentAmount:
      Number(option?.installmentAmount) ||
      (selectedInstallments === recurrence ? Number(installmentAmount) : 0) ||
      Number(negotiatedAmount || 0) / selectedInstallments
  }
}

export const getEnrollmentFeePaymentOption = (
  enrollmentFee?: InstitutionContractTemplateEnrollmentFeeType | null,
  paymentMethodUid?: string | null
) => {
  const { paymentMethodOptions = {} } = enrollmentFee || {}
  for (const type of Object.keys(paymentMethodOptions)) {
    const option = paymentMethodOptions[type]?.find(
      item => !!paymentMethodUid && item.paymentMethod?.uid === paymentMethodUid
    )
    if (option) return { type, option }
  }
  return { type: null, option: null }
}

export const getEnrollmentFeeInstallment = (
  enrollmentFee?: InstitutionContractTemplateEnrollmentFeeType | null,
  paymentMethodUid?: string | null,
  installments?: number | null
) => {
  const { option } = getEnrollmentFeePaymentOption(enrollmentFee, paymentMethodUid)
  return ((option?.installments || []) as InstallmentsType[]).find(
    item => item.installments === (installments || 1)
  )
}

export const getEnrollmentFeeTotal = (
  enrollmentFee?: InstitutionContractTemplateEnrollmentFeeType | null,
  paymentMethodUid?: string | null,
  installments?: number | null
) => {
  const { option } = getEnrollmentFeePaymentOption(enrollmentFee, paymentMethodUid)
  const installment = getEnrollmentFeeInstallment(enrollmentFee, paymentMethodUid, installments)
  return (
    Number(installment?.total || 0) ||
    Number(option?.total || 0) ||
    Number(enrollmentFee?.amount || 0)
  )
}

const stepStages: Partial<Record<ApplicationStepKey, EnrollmentApplicationStageType>> = {
  'registration-data': 'registration_data',
  form: 'form',
  'institution-contract': 'institution_contract',
  review: 'review'
}

export default function useApplication(enabled = true) {
  const router = useRouter()
  const { isAuthenticated } = useAuth() || {}
  const siteSlug = getUrlString(router?.query?.site)
  const applicationUid = getUrlString(router?.query?.applicationUid)

  const {
    data: application,
    error,
    isLoading,
    mutate
  }: {
    data?: EnrollmentApplicationType
    error?: any
    isLoading: boolean
    mutate: () => Promise<any>
  } = useFetch(
    [
      siteSlug && applicationUid && enabled && isAuthenticated
        ? `/${siteSlug}/enrollment-store/enrollment-applications/${applicationUid}/`
        : null
    ],
    { revalidateOnFocus: false, retryCount: 3 }
  )
  const { data: offering }: { data?: EnrollmentCourseOfferingType } = useFetch([
    siteSlug && application?.offering?.uid
      ? `/${siteSlug}/enrollment-store/course-offerings/${application.offering.uid}`
      : null
  ])

  const hasNegotiationStep = !!application?.availableStages?.includes('institution_contract')
  const hasFormStep = !!application?.formResponse
  const steps = getApplicationSteps(applicationUid, hasNegotiationStep, hasFormStep)

  const getStepStage = (key: ApplicationStepKey): EnrollmentApplicationStageType | null => {
    const stage = stepStages[key]
    return stage && application?.availableStages?.includes(stage) ? stage : null
  }

  const goToStep = async (step?: ApplicationStep) => {
    if (!step) return
    const currentStage = getStepStage(step.key)
    if (currentStage) {
      try {
        await api.patch(
          `/${siteSlug}/enrollment-store/enrollment-applications/${applicationUid}/`,
          { current_stage: currentStage }
        )
        await mutate()
      } catch (error: any) {
        const errors = errorHandler(error?.response?.data)
        notificationHandler({
          variant: 'error',
          message: errors?.messages?.[0] || 'Houve um erro ao salvar a etapa. Tente novamente.'
        })
        return
      }
    }
    router.push(step.path)
  }

  return {
    applicationUid,
    application,
    offering,
    error,
    isLoading,
    mutate,
    steps,
    hasNegotiationStep,
    hasFormStep,
    getStepStage,
    goToStep
  }
}
